import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export default function Subscribe() {
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [payingOnline, setPayingOnline] = useState(false);
  const { user, subscription, refreshUser } = useAuth();

  useEffect(() => {
    API.get('/admin/plans').then(r => setPlans(r.data));
    API.get('/admin/settings').then(r => setSettings(r.data));
  }, [user]);

  const now = new Date();
  const subActive = subscription && subscription.status === 'active' && new Date(subscription.end_date) > now;
  const plan = plans.find(p => p.id === selectedPlan);

  const hasPlanUpi = plan?.plan_upi_id;
  const hasPlanQr = plan?.plan_qr_image;
  const hasPlanLink = plan?.plan_payment_link;
  const hasPlanPayment = hasPlanUpi || hasPlanQr || hasPlanLink;
  const hasGeneralUpi = settings.upi_id;
  const hasGeneralQr = settings.upi_qr;
  const hasGeneralLink = settings.payment_link;
  const hasGeneralPayment = hasGeneralUpi || hasGeneralQr || hasGeneralLink;
  const hasRazorpay = settings.razorpay_key_id && settings.razorpay_key_id.startsWith('rzp_');

  const loadRazorpayScript = () => new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const payWithRazorpay = async () => {
    if (!selectedPlan) return toast.error('Select a plan first');
    setPayingOnline(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) return toast.error('Failed to load Razorpay. Check your internet.');

      const { data } = await API.post('/payment/create-order', { plan_id: selectedPlan });

      const options = {
        key: data.key_id,
        amount: data.order.amount,
        currency: 'INR',
        name: settings.brand_name || 'Oexams',
        description: `${data.plan.name} Subscription`,
        order_id: data.order.id,
        handler: async (response) => {
          try {
            await API.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedPlan,
            });
            toast.success('🎉 Payment successful! Subscription activated.');
            await refreshUser();
            setSubmitted(true);
          } catch (err) {
            toast.error('Payment verification failed. Contact support.');
          }
        },
        prefill: { name: user?.full_name, contact: user?.phone },
        theme: { color: '#6366f1' },
        modal: { ondismiss: () => setPayingOnline(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create order');
    } finally {
      setPayingOnline(false);
    }
  };

  const submit = async () => {
    if (!selectedPlan) return toast.error('Select a plan first');
    if (!screenshot) return toast.error('Upload payment screenshot');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('plan_id', selectedPlan);
      fd.append('payment_note', note);
      fd.append('payment_method', paymentMethod);
      fd.append('screenshot', screenshot);
      await API.post('/admin/subscribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
      toast.success('✅ Request submitted! Admin will activate your plan shortly.');
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setSubmitting(false); }
  };

  const copyText = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied!`); };

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth:800, margin:'0 auto', padding:'32px 20px' }}>
        <div className="page-header"><h1>💳 Subscribe</h1><p>Choose a plan to host exams</p></div>

        {subActive && (
          <div className="alert alert-success" style={{ marginBottom:24 }}>
            ✅ Current: <strong>{subscription.plan_name}</strong> — expires {new Date(subscription.end_date).toLocaleDateString()} • {subscription.exam_limit - subscription.exams_used} exams left
          </div>
        )}

        {submitted ? (
          <div className="card text-center" style={{ padding:'48px 20px' }}>
            <div style={{ fontSize:64, marginBottom:16 }}>🎉</div>
            <h2 style={{ marginBottom:8 }}>
              {payingOnline === false && paymentMethod === '' ? 'Payment Successful!' : 'Request Submitted!'}
            </h2>
            <p style={{ color:'var(--text2)', marginBottom:24 }}>
              Your subscription is now active. Start creating exams!
            </p>
            <button className="btn btn-primary" onClick={() => window.location.href='/#/teacher'}>🏠 Go to Dashboard</button>
          </div>
        ) : (
          <>
            {/* Step 1 — Choose Plan */}
            <div style={{ marginBottom:8, fontWeight:700, color:'var(--text2)', fontSize:13 }}>STEP 1 — CHOOSE A PLAN</div>
            <div className="grid-3" style={{ marginBottom:28 }}>
              {plans.map(p => (
                <div key={p.id} onClick={() => { setSelectedPlan(p.id); setPaymentMethod(''); }} className="card" style={{ cursor:'pointer', border:`2px solid ${selectedPlan===p.id?'var(--primary)':'var(--border)'}`, transition:'all 0.2s', transform:selectedPlan===p.id?'scale(1.02)':'scale(1)' }}>
                  {selectedPlan===p.id && <div style={{ textAlign:'right', color:'var(--primary-light)', fontWeight:700, fontSize:12, marginBottom:4 }}>✅ Selected</div>}
                  <div style={{ fontWeight:800, fontSize:22, color:'var(--primary-light)', marginBottom:4 }}>₹{p.price}</div>
                  <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{p.name}</div>
                  {p.description && <p style={{ fontSize:12, color:'var(--text2)', marginBottom:10 }}>{p.description}</p>}
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                    <span className="badge badge-info">📅 {p.duration_days} days</span>
                    <span className="badge badge-success">📝 {p.exam_limit} exams</span>
                  </div>
                </div>
              ))}
              {plans.length === 0 && <div className="card text-center text-muted" style={{ gridColumn:'1/-1' }}>No plans available. Contact admin.</div>}
            </div>

            {selectedPlan && (
              <>
                <div style={{ marginBottom:8, fontWeight:700, color:'var(--text2)', fontSize:13 }}>STEP 2 — PAY ₹{plan?.price}</div>

                {/* ── RAZORPAY INSTANT PAYMENT ── */}
                {hasRazorpay && (
                  <div className="card" style={{ marginBottom:16, border:'1px solid rgba(99,102,241,0.4)', background:'rgba(99,102,241,0.06)' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
                      <div>
                        <div style={{ fontWeight:800, fontSize:16, color:'var(--text)', marginBottom:4 }}>⚡ Pay Online — Instant Activation</div>
                        <div style={{ fontSize:13, color:'var(--text2)' }}>Pay ₹{plan?.price} via UPI, Card, Net Banking. Subscription activates immediately.</div>
                      </div>
                      <button
                        onClick={payWithRazorpay}
                        disabled={payingOnline}
                        style={{ background:'#6366f1', color:'white', border:'none', borderRadius:10, padding:'12px 28px', fontWeight:800, fontSize:15, cursor:'pointer', whiteSpace:'nowrap', opacity: payingOnline ? 0.7 : 1 }}
                      >
                        {payingOnline ? '⏳ Loading...' : `💳 Pay ₹${plan?.price} Now`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Divider */}
                {hasRazorpay && (hasPlanPayment || hasGeneralPayment) && (
                  <div style={{ display:'flex', alignItems:'center', gap:12, margin:'16px 0' }}>
                    <div style={{ flex:1, height:1, background:'var(--border)' }} />
                    <span style={{ fontSize:12, color:'var(--text3)', fontWeight:600 }}>OR PAY MANUALLY</span>
                    <div style={{ flex:1, height:1, background:'var(--border)' }} />
                  </div>
                )}

                <div className="card" style={{ marginBottom:24 }}>
                  {hasPlanPayment && (
                    <div style={{ marginBottom: hasGeneralPayment ? 20 : 0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                        <span style={{ background:'rgba(16,185,129,0.2)', color:'#34d399', padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>✅ Recommended — Plan Specific (₹{plan.price})</span>
                      </div>
                      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
                        {hasPlanUpi && (
                          <div style={{ flex:1, minWidth:180, background:'var(--card2)', borderRadius:10, padding:14, border:'1px solid var(--border)' }}>
                            <div style={{ fontWeight:700, marginBottom:8 }}>💸 UPI ID</div>
                            <div style={{ fontFamily:'var(--mono)', fontSize:16, color:'var(--primary-light)', background:'var(--bg)', padding:'8px 10px', borderRadius:8, marginBottom:8, wordBreak:'break-all' }}>{plan.plan_upi_id}</div>
                            <button onClick={() => { copyText(plan.plan_upi_id, 'UPI ID'); setPaymentMethod('plan'); }} className="btn btn-secondary btn-sm btn-full">📋 Copy UPI ID</button>
                          </div>
                        )}
                        {hasPlanQr && (
                          <div style={{ flex:1, minWidth:180, background:'var(--card2)', borderRadius:10, padding:14, border:'1px solid var(--border)', textAlign:'center' }}>
                            <div style={{ fontWeight:700, marginBottom:10 }}>📱 Scan QR (₹{plan.price})</div>
                            <img src={plan.plan_qr_image} alt="QR" style={{ width:130, height:130, objectFit:'contain', borderRadius:8, border:'4px solid white' }} />
                            <button onClick={() => setPaymentMethod('plan')} className="btn btn-secondary btn-sm btn-full" style={{ marginTop:8 }}>✅ I scanned this</button>
                          </div>
                        )}
                        {hasPlanLink && (
                          <div style={{ flex:1, minWidth:180, background:'var(--card2)', borderRadius:10, padding:14, border:'1px solid var(--border)' }}>
                            <div style={{ fontWeight:700, marginBottom:8 }}>🔗 Payment Link</div>
                            <a href={plan.plan_payment_link} target="_blank" rel="noreferrer" onClick={() => setPaymentMethod('plan')} className="btn btn-primary btn-full">💳 Pay ₹{plan.price} Now</a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {hasPlanPayment && hasGeneralPayment && (
                    <div style={{ display:'flex', alignItems:'center', gap:12, margin:'16px 0' }}>
                      <div style={{ flex:1, height:1, background:'var(--border)' }} />
                      <span style={{ fontSize:12, color:'var(--text3)', fontWeight:600 }}>OR USE GENERAL PAYMENT</span>
                      <div style={{ flex:1, height:1, background:'var(--border)' }} />
                    </div>
                  )}

                  {hasGeneralPayment && (
                    <div>
                      {hasPlanPayment && <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}><span style={{ background:'rgba(245,158,11,0.2)', color:'#fcd34d', padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>⚠️ General Payment — Mention plan name in note</span></div>}
                      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
                        {hasGeneralUpi && (
                          <div style={{ flex:1, minWidth:180, background:'var(--card2)', borderRadius:10, padding:14, border:'1px solid var(--border)' }}>
                            <div style={{ fontWeight:700, marginBottom:8 }}>💸 General UPI</div>
                            <div style={{ fontFamily:'var(--mono)', fontSize:15, color:'var(--primary-light)', background:'var(--bg)', padding:'8px 10px', borderRadius:8, marginBottom:8, wordBreak:'break-all' }}>{settings.upi_id}</div>
                            <button onClick={() => { copyText(settings.upi_id, 'UPI ID'); setPaymentMethod('general'); }} className="btn btn-secondary btn-sm btn-full">📋 Copy UPI ID</button>
                          </div>
                        )}
                        {hasGeneralQr && (
                          <div style={{ flex:1, minWidth:180, background:'var(--card2)', borderRadius:10, padding:14, border:'1px solid var(--border)', textAlign:'center' }}>
                            <div style={{ fontWeight:700, marginBottom:10 }}>📱 General QR Code</div>
                            <img src={settings.upi_qr} alt="QR" style={{ width:120, height:120, objectFit:'contain', borderRadius:8, border:'4px solid white' }} />
                            <button onClick={() => setPaymentMethod('general')} className="btn btn-secondary btn-sm btn-full" style={{ marginTop:8 }}>✅ I scanned this</button>
                          </div>
                        )}
                        {hasGeneralLink && (
                          <div style={{ flex:1, minWidth:180, background:'var(--card2)', borderRadius:10, padding:14, border:'1px solid var(--border)' }}>
                            <div style={{ fontWeight:700, marginBottom:8 }}>🔗 General Link</div>
                            <a href={settings.payment_link} target="_blank" rel="noreferrer" onClick={() => setPaymentMethod('general')} className="btn btn-secondary btn-full">💳 Open Payment Link</a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!hasPlanPayment && !hasGeneralPayment && !hasRazorpay && (
                    <div className="alert alert-warning">⚠️ No payment options configured. Contact admin directly.</div>
                  )}
                </div>

                {/* Step 3 — Screenshot (only for manual payment) */}
                {(hasPlanPayment || hasGeneralPayment) && (
                  <>
                    <div style={{ marginBottom:8, fontWeight:700, color:'var(--text2)', fontSize:13 }}>STEP 3 — UPLOAD SCREENSHOT (for manual payment only)</div>
                    <div className="card">
                      {paymentMethod === 'general' && (
                        <div className="alert alert-warning" style={{ marginBottom:16, fontSize:13 }}>
                          ⚠️ You used general payment. Please mention <strong>plan name "{plan?.name}" and amount ₹{plan?.price}</strong> in the note below.
                        </div>
                      )}
                      <div className="form-group">
                        <label>Payment Screenshot *</label>
                        <input type="file" accept="image/*" onChange={e => setScreenshot(e.target.files[0])} style={{ padding:8 }} />
                      </div>
                      <div className="form-group">
                        <label>Transaction ID / Note</label>
                        <input value={note} onChange={e => setNote(e.target.value)} placeholder={`e.g. TXN123 • Plan: ${plan?.name} • ₹${plan?.price}`} />
                      </div>
                      <button className="btn btn-primary btn-lg btn-full" onClick={submit} disabled={submitting}>
                        {submitting ? '⏳ Submitting...' : '🚀 Submit Payment Request'}
                      </button>
                      <p style={{ fontSize:11, color:'var(--text3)', marginTop:10, textAlign:'center' }}>Admin will verify and activate your plan within a few hours.</p>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
